<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
    
    <!-- 1. Sidebar (Handles its own mobile overlay logic) -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <!-- md:ml-64 pushes this entire section to the right of the sidebar on desktop. -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">
      
      <!-- 3. Topbar (Sticky, stays at top of this wrapper) -->
      <header class="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Scrollable Page Content -->
      <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        
        <!-- Loading State -->
        <div v-if="!applicantInfo" class="flex flex-col justify-center items-center h-64">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#74bd54]"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading applicant information...</p>
        </div>

        <div v-else class="space-y-6">
          
          <!-- 1. Applicant Information Card -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div>
                  <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ applicantInfo.full_name || 'Not Available' }}</h2>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Applying for: <span class="font-medium text-gray-700 dark:text-gray-300">{{ applicantInfo.position_title || 'Not specified' }}</span>
                  </p>
                </div>
                <div class="flex flex-col sm:items-end gap-1">
                  <span :class="getStatusBadge(applicantInfo.applicant_status)" class="px-3 py-1 rounded-full text-xs font-semibold capitalize w-fit">
                    {{ applicantInfo.applicant_status || 'Pending' }}
                  </span>
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    Entry: {{ applicantInfo.entry_status || 'N/A' }}
                  </span>
                </div>
              </div>
            </div>
            
            <div class="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white mt-1">{{ applicantInfo.email || 'Not available' }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white mt-1">{{ applicantInfo.phone || 'Not available' }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Period</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white mt-1">{{ applicantInfo.period || 'Not specified' }}</p>
              </div>
              <div>
                <p class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date Shortlisted</p>
                <p class="text-sm font-medium text-gray-900 dark:text-white mt-1">{{ formatDate(applicantInfo.created_at) }}</p>
              </div>
            </div>

            <!-- Interview Schedule -->
            <div v-if="interviewSchedule && interviewSchedule.interview_date" class="px-6 pb-6">
              <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
                <h3 class="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  Interview Schedule
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p class="text-xs text-blue-700 dark:text-blue-400">Date</p>
                    <p class="text-sm font-medium text-blue-900 dark:text-blue-200">{{ formatDate(interviewSchedule.interview_date) }}</p>
                  </div>
                  <div>
                    <p class="text-xs text-blue-700 dark:text-blue-400">Time</p>
                    <p class="text-sm font-medium text-blue-900 dark:text-blue-200">{{ interviewSchedule.interview_time }}</p>
                  </div>
                  <div v-if="interviewSchedule.notes" class="sm:col-span-1">
                    <p class="text-xs text-blue-700 dark:text-blue-400">Notes</p>
                    <p class="text-sm text-blue-900 dark:text-blue-200">{{ interviewSchedule.notes }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Documents Section (Consolidated & Cleaned) -->
            <div class="px-6 pb-6 space-y-4">
              <!-- CVs -->
              <div v-if="applicantCVs.length > 0 || applicantInfo.cv_file_path">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  Curriculum Vitae
                </h3>
                <div class="flex flex-wrap gap-3">
                  <template v-for="(cv, index) in (applicantCVs.length > 0 ? applicantCVs : [{ file_path: applicantInfo.cv_file_path }])" :key="cv.id || index">
                    <div class="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <span class="text-xl">📄</span>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ cv.file_name || `CV ${applicantCVs.length > 1 ? index + 1 : ''}` }}</p>
                      </div>
                      <div class="flex gap-2">
                        <button @click="viewCV(cv.file_path)" class="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition" title="View">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        <button @click="downloadCV(cv.file_path)" class="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition" title="Download">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                        </button>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Attachments -->
              <div v-if="applicantInfo.attachments && applicantInfo.attachments.length > 0">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                  Pre-Interview Documents
                </h3>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div v-for="(att, index) in applicantInfo.attachments" :key="att.id || index" class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div class="flex items-center gap-3 min-w-0">
                      <span class="text-xl flex-shrink-0">{{ getFileIcon(att.file_type) }}</span>
                      <span class="text-sm font-medium text-gray-900 dark:text-white truncate" :title="att.file_name || att.file_path.split('/').pop()">
                        {{ att.file_name || att.file_path.split('/').pop() }}
                      </span>
                    </div>
                    <div class="flex gap-2 flex-shrink-0">
                      <button @click="viewCV(att.file_path)" class="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition" title="View">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      </button>
                      <button @click="downloadCV(att.file_path)" class="p-1.5 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md transition" title="Download">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No Documents Message -->
              <div v-if="!applicantInfo.cv_file_path && applicantCVs.length === 0 && (!applicantInfo.attachments || applicantInfo.attachments.length === 0)" class="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-center">
                <p class="text-sm text-gray-500 dark:text-gray-400">No documents or CV uploaded yet.</p>
              </div>

              <!-- Recommendation Info -->
              <div v-if="applicantInfo.recommendation_type" class="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Recommendation Source</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  <span class="font-medium text-gray-900 dark:text-white capitalize">{{ applicantInfo.recommendation_type }}</span>
                  <span v-if="applicantInfo.recommendation_name"> — {{ applicantInfo.recommendation_name }}</span>
                </p>
                <p v-if="applicantInfo.recommendation_text" class="text-sm text-gray-600 dark:text-gray-400 mt-2 italic">"{{ applicantInfo.recommendation_text }}"</p>
              </div>

              <!-- Applicant Comment -->
              <div v-if="applicantInfo.applicant_comment" class="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Applicant Comment</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">{{ applicantInfo.applicant_comment }}</p>
              </div>
            </div>
          </div>

          <!-- 2. Interview Evaluation Criteria -->
          <div v-if="evaluationCriteria.length > 0" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <svg class="w-5 h-5 text-[#74bd54]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                Interview Evaluation Criteria
              </h3>
            </div>
            
            <div class="p-6 space-y-8">
              <div v-for="criteria in evaluationCriteria" :key="criteria.id" class="border-b border-gray-100 dark:border-gray-700 pb-6 last:border-0 last:pb-0">
                <div class="mb-4">
                  <h4 class="font-semibold text-gray-900 dark:text-white">{{ criteria.name }}</h4>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Rate from 1 to {{ criteria.max_score || 10 }}</p>
                </div>

                <!-- Radio Button Rating Group -->
                <div class="mb-4">
                  <div class="flex flex-wrap gap-2">
                    <label 
                      v-for="score in generateRatingOptions(criteria.max_score || 10)" 
                      :key="score" 
                      class="relative flex items-center justify-center cursor-pointer group"
                    >
                      <input 
                        type="radio"
                        :value="score"
                        v-model="evaluationRatings[criteria.id]"
                        class="sr-only peer"
                        :name="`rating_${criteria.id}`"
                      />
                      <div class="w-10 h-10 rounded-full border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-400 peer-checked:bg-[#74bd54] peer-checked:border-[#74bd54] peer-checked:text-white transition-all duration-200 group-hover:border-[#74bd54] group-hover:scale-105">
                        {{ score }}
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Comments for this criteria -->
                <div>
                  <textarea 
                    v-model="evaluationComments[criteria.id]"
                    placeholder="Add specific comments for this criteria..."
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition resize-none"
                    rows="2"
                  ></textarea>
                </div>
              </div>

              <!-- Overall Interview Comments -->
              <div class="pt-6 border-t border-gray-100 dark:border-gray-700">
                <label class="block font-semibold text-gray-900 dark:text-white mb-2">Overall Interview Comments</label>
                <textarea 
                  v-model="overallComments"
                  placeholder="Provide overall feedback about the candidate..."
                  class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition resize-none"
                  rows="4"
                ></textarea>
              </div>

              <!-- Recommendation Decision -->
              <div class="pt-6 border-t border-gray-100 dark:border-gray-700">
                <label class="block font-semibold text-gray-900 dark:text-white mb-3">Final Recommendation</label>
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <label v-if="hasPermission('manpower', 'validate')" class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-green-50 dark:hover:bg-green-900/20 transition has-[:checked]:border-green-500 has-[:checked]:bg-green-50 dark:has-[:checked]:bg-green-900/20">
                    <input type="radio" v-model="recommendation" value="Validated" class="w-4 h-4 text-green-600 focus:ring-green-500 mr-3">
                    <span class="text-sm font-medium text-green-700 dark:text-green-400">Validate / Hire</span>
                  </label>
                  <label v-if="hasPermission('manpower', 'second_interview')" class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition has-[:checked]:border-yellow-500 has-[:checked]:bg-yellow-50 dark:has-[:checked]:bg-yellow-900/20">
                    <input type="radio" v-model="recommendation" value="second_interview" class="w-4 h-4 text-yellow-600 focus:ring-yellow-500 mr-3">
                    <span class="text-sm font-medium text-yellow-700 dark:text-yellow-400">Second Interview</span>
                  </label>
                  <label v-if="hasPermission('manpower', 'consider')" class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 dark:has-[:checked]:bg-blue-900/20">
                    <input type="radio" v-model="recommendation" value="consider" class="w-4 h-4 text-blue-600 focus:ring-blue-500 mr-3">
                    <span class="text-sm font-medium text-blue-700 dark:text-blue-400">Consider for Other Role</span>
                  </label>
                  <label v-if="hasPermission('manpower', 'reject')" class="flex items-center p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition has-[:checked]:border-red-500 has-[:checked]:bg-red-50 dark:has-[:checked]:bg-red-900/20">
                    <input type="radio" v-model="recommendation" value="reject" class="w-4 h-4 text-red-600 focus:ring-red-500 mr-3">
                    <span class="text-sm font-medium text-red-700 dark:text-red-400">Reject</span>
                  </label>
                </div>
              </div>

              <!-- Rating Summary -->
              <div v-if="hasSelectedRatings" class="pt-6 border-t border-gray-100 dark:border-gray-700">
                <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 border border-gray-200 dark:border-gray-600">
                  <h4 class="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <svg class="w-4 h-4 text-[#74bd54]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                    Rating Summary
                  </h4>
                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                    <div v-for="criteria in evaluationCriteria" :key="criteria.id" class="flex justify-between items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700">
                      <span class="text-sm text-gray-600 dark:text-gray-400 truncate mr-2">{{ criteria.name }}</span>
                      <span class="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">
                        {{ evaluationRatings[criteria.id] || '-' }}
                        <span v-if="evaluationRatings[criteria.id]" class="text-xs text-gray-400 font-normal">/ {{ criteria.max_score || 10 }}</span>
                      </span>
                    </div>
                  </div>
                  <div v-if="calculateTotalScore()" class="pt-4 border-t border-gray-200 dark:border-gray-600 grid grid-cols-2 gap-4">
                    <div class="flex justify-between items-center">
                      <span class="font-semibold text-gray-700 dark:text-gray-300">Total Score:</span>
                      <span class="font-bold text-xl text-[#74bd54]">{{ calculateTotalScore() }}</span>
                    </div>
                    <div class="flex justify-between items-center">
                      <span class="font-semibold text-gray-700 dark:text-gray-300">Average Score:</span>
                      <span class="font-bold text-xl text-[#74bd54]">{{ calculateAverageScore() }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 3. Interview Questions -->
          <div v-if="mainQuestions.length > 0" class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <h3 class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <svg class="w-5 h-5 text-[#74bd54]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-2 2v-2z" /></svg>
                Interview Questions & Answers
              </h3>
            </div>
            
            <div class="p-6 space-y-6">
              <div v-for="main in mainQuestions" :key="main.id" class="p-5 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-700">
                <div class="mb-4">
                  <span class="inline-block px-2 py-1 text-xs font-semibold text-[#74bd54] bg-[#74bd54]/10 rounded-md mb-2">{{ main.category || 'General' }}</span>
                  <h4 class="font-semibold text-gray-900 dark:text-white text-lg">{{ main.question_text }}</h4>
                </div>

                <!-- Main Answer -->
                <div class="mb-4">
                  <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Candidate's Answer</label>
                  <textarea 
                    v-model="answers[main.id].answer"
                    placeholder="Enter answer here..."
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition resize-none"
                    rows="3"
                  ></textarea>
                </div>

                <!-- Subquestions -->
                <div v-if="subQuestions[main.id]?.length" class="ml-0 sm:ml-6 space-y-4 border-l-2 border-gray-200 dark:border-gray-600 pl-4">
                  <div v-for="sub in subQuestions[main.id]" :key="sub.id">
                    <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{{ sub.question_text }}</p>
                    <textarea 
                      v-model="answers[sub.id].answer"
                      placeholder="Enter answer here..."
                      class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition resize-none"
                      rows="2"
                    ></textarea>
                  </div>
                </div>

                <!-- Notes / Remarks -->
                <div class="mt-4">
                  <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Interviewer Notes / Remarks</label>
                  <input 
                    type="text"
                    v-model="answers[main.id].notes"
                    placeholder="Add notes or remarks..."
                    class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#74bd54] focus:border-transparent transition" 
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 4. Action Buttons -->
          <div class="flex flex-col sm:flex-row justify-end gap-4 pt-4 pb-8">
            <button 
              @click="goBack"
              type="button"
              class="inline-flex justify-center items-center px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
              Back to List
            </button>
            <button 
              v-if="hasPermission('manpower', 'submit_interview')" 
              @click="submitInterview"
              :disabled="isSubmitting"
              class="inline-flex justify-center items-center px-6 py-3 bg-[#74bd54] hover:bg-[#63a43f] disabled:opacity-70 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium shadow-sm shadow-[#74bd54]/30 transition-all active:scale-95"
            >
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isSubmitting ? (isEditMode ? 'Updating...' : 'Submitting...') : (isEditMode ? 'Update Interview' : 'Submit Interview') }}
            </button>
          </div>

        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import api from '@/services/api'
import HrSidebar from '../../components/hr/HrSidebar.vue'
import HrTopbar from '../../components/hr/HrTopbar.vue'
import { useToast } from 'vue-toastification'
import { usePermissions } from '@/composables/usePermissions'

const route = useRoute()
const applicantId = route.params.id
const toast = useToast()
const { hasPermission } = usePermissions()

const backendURL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/"

const isSidebarOpen = ref(false) // Hidden by default on mobile
const isDarkMode = ref(false)
const currentPageTitle = ref('First Interview')
const isSubmitting = ref(false)

const applicantInfo = ref(null)
const interviewSchedule = ref(null)
const applicantCVs = ref([])

const evaluationCriteria = ref([])
const evaluationRatings = ref({})
const evaluationComments = ref({})
const overallComments = ref('')
const recommendation = ref('')

const mainQuestions = ref([])
const subQuestions = ref({})
const answers = ref({})

const existingInterview = ref(null)
const isEditMode = ref(false)

const getFullFileUrl = (filePath) => {
  if (!filePath) return null
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath
  const cleanPath = filePath.replace(/^\/+/, '')
  return `${backendURL}${cleanPath}`
}

const getFileIcon = (fileType) => {
  if (!fileType) return '📄'
  const type = fileType.toLowerCase()
  if (type.includes('pdf')) return '📕'
  if (type.includes('jpg') || type.includes('jpeg') || type.includes('png')) return '🖼️'
  if (type.includes('doc') || type.includes('docx')) return '📘'
  if (type.includes('photo')) return '📸'
  if (type.includes('txt')) return '📃'
  return '📄'
}

const goBack = () => {
  window.history.back()
}

const fetchExistingInterview = async () => {
  try {
    const response = await api.get(`GetInterviewAnswers.php?applicant_id=${applicantId}`)
    if (response.data?.status === 'success' && response.data?.data) {
      const data = response.data.data
      existingInterview.value = data
      isEditMode.value = true
      
      if (Array.isArray(data.answers)) {
        data.answers.forEach(ans => {
          if (answers.value[ans.question_id]) {
            answers.value[ans.question_id].answer = ans.answer || ''
            answers.value[ans.question_id].notes = ans.notes || ''
          }
        })
      }
      
      if (data.evaluation) {
        if (data.evaluation.criteria_ratings) {
          Object.entries(data.evaluation.criteria_ratings).forEach(([cid, rating]) => {
            if (evaluationRatings.value[cid] !== undefined) evaluationRatings.value[cid] = rating
          })
        }
        if (data.evaluation.criteria_comments) {
          Object.entries(data.evaluation.criteria_comments).forEach(([cid, comment]) => {
            if (evaluationComments.value[cid] !== undefined) evaluationComments.value[cid] = comment
          })
        }
        overallComments.value = data.evaluation.overall_comments || ''
        recommendation.value = data.evaluation.recommendation || ''
      }
    }
  } catch (err) {
    console.log('ℹ️ No existing interview (new interview)')
  }
}

const fetchEvaluationCriteria = async () => {
  try {
    const response = await api.get('InterviewEvaluationCriteria.php')
    if (Array.isArray(response.data)) {
      evaluationCriteria.value = response.data
      evaluationCriteria.value.forEach(criteria => {
        evaluationRatings.value[criteria.id] = ''
        evaluationComments.value[criteria.id] = ''
      })
    } else if (response.data && response.data.criteria) {
      evaluationCriteria.value = response.data.criteria
      evaluationCriteria.value.forEach(criteria => {
        evaluationRatings.value[criteria.id] = ''
        evaluationComments.value[criteria.id] = ''
      })
    }
  } catch (err) {
    console.error('Error fetching evaluation criteria:', err)
  }
}

const hasSelectedRatings = computed(() => {
  return Object.values(evaluationRatings.value).some(rating => rating !== '' && rating !== null)
})

const generateRatingOptions = (maxScore) => {
  const options = []
  for (let i = 1; i <= maxScore; i++) options.push(i)
  return options
}

const fetchApplicantInfo = async () => {
  try {
    const response = await api.get(`GetApplicantDetails.php?id=${applicantId}`)
    if (response.data && response.data.success) {
      applicantInfo.value = response.data.data.applicant
      interviewSchedule.value = response.data.data.interview
      applicantCVs.value = response.data.data.cvs || []
      applicantInfo.value.attachments = response.data.attachments || []
    }
  } catch (err) {
    console.error('Error fetching applicant info:', err)
  }
}

const fetchQuestions = async () => {
  try {
    const response = await api.get('InterviewQuestions.php')
    const questions = response.data
    mainQuestions.value = questions.filter(q => !q.parent_id)
    subQuestions.value = {}
    mainQuestions.value.forEach(main => {
      subQuestions.value[main.id] = questions.filter(q => q.parent_id === main.id)
    })
    questions.forEach(q => {
      answers.value[q.id] = { answer: '', notes: '' }
    })
  } catch (err) {
    console.error('Error fetching questions:', err)
  }
}

const calculateTotalScore = () => {
  let total = 0
  let count = 0
  evaluationCriteria.value.forEach(criteria => {
    const rating = evaluationRatings.value[criteria.id]
    if (rating && !isNaN(parseInt(rating))) {
      total += parseInt(rating)
      count++
    }
  })
  return count > 0 ? total : null
}

const calculateAverageScore = () => {
  const total = calculateTotalScore()
  const count = evaluationCriteria.value.filter(criteria => 
    evaluationRatings.value[criteria.id] && !isNaN(parseInt(evaluationRatings.value[criteria.id]))
  ).length
  return count > 0 ? (total / count).toFixed(1) : null
}

const submitInterview = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  const MIN_LOADING_MS = 2000
  const startTime = Date.now()
  const loadingMessage = isEditMode.value ? "Updating interview..." : "Submitting interview..."
  const toastId = toast.info(loadingMessage, { timeout: false })

  try {
    const payload = {
      applicant_id: applicantId,
      answers: answers.value,
      interview_date: interviewSchedule.value?.interview_date,
      evaluation: {
        criteria_ratings: evaluationRatings.value,
        criteria_comments: evaluationComments.value,
        overall_comments: overallComments.value,
        recommendation: recommendation.value,
        total_score: calculateTotalScore(),
        average_score: calculateAverageScore()
      }
    }

    if (isEditMode.value && existingInterview.value?.id) {
      payload.interview_id = existingInterview.value.id
    }

    const response = await api.post('SubmitInterview.php', payload)

    if (response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Submission failed')
    }

    toast.success(isEditMode.value ? "Interview updated successfully!" : "Interview submitted successfully!")

  } catch (err) {
    console.error(err)
    const errorMsg = err.response?.data?.message || err.message || "Error submitting interview"
    toast.error(errorMsg)
  } finally {
    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed)
    if (remaining > 0) await new Promise(res => setTimeout(res, remaining))
    toast.dismiss(toastId)
    isSubmitting.value = false
  }
}

const viewCV = (cvPath) => {
  if (cvPath) {
    window.open(getFullFileUrl(cvPath), '_blank')
  } else {
    toast.warning('CV file path not available')
  }
}

const downloadCV = (cvPath) => {
  if (cvPath) {
    const fullUrl = getFullFileUrl(cvPath)
    const link = document.createElement('a')
    link.href = fullUrl
    link.download = cvPath.split('/').pop()
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } else {
    toast.warning('CV file path not available')
  }
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  } catch (e) {
    return dateString
  }
}

const getStatusBadge = (status) => {
  const badges = {
    'New': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'Processing': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'Interviewed': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
    'Hired': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    'Rejected': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'Pending': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
  }
  return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
}

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }
const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  document.documentElement.classList.toggle('dark', isDarkMode.value)
}

onMounted(() => {
  fetchApplicantInfo()
  fetchQuestions()
  fetchEvaluationCriteria()
  fetchExistingInterview()
})
</script>